// src/components/AuthProvider.js
import { useContext, createContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import APIService from "../Service/APIService";

const AuthContext = createContext(null);

const PUBLIC_PATHS = new Set([
  "/login",
  "/forgot",
  "/reset",
  "/setpassword",
  "/register",
  "/openorder",
  "/openorder/status",
  "/openorder/vieworder",
]);

export const isPublicPath = (pathname) => {
  const normalized = String(pathname ?? "")
    .trim()
    .toLowerCase()
    .replace(/\/+$/, "");
  return PUBLIC_PATHS.has(normalized || "/");
};

const redirectToExpiredPasswordReset = (email) => {
  const query = email ? `?reason=password_expired&email=${encodeURIComponent(email)}` : "?reason=password_expired";
  if (window.location.pathname.toLowerCase() !== "/forgot") {
    window.location.replace(`/Forgot${query}`);
  }
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);        // in-memory only
  const [isReady, setIsReady] = useState(false); // becomes true once bootstrap completes (or is skipped)
  const location = useLocation();

  // Bootstrap on first mount (unless we're on a public route)
  useEffect(() => {
    let mounted = true;

    const doBootstrap = async () => {
      const path = location.pathname.toLowerCase();
      console.log("AuthProvider checking public path", path);
      // Skip refresh/me on public screens to avoid “missing refresh token” noise
      if (isPublicPath(path)) {
        console.log("Skipping auth bootstrap on public path", path);
        if (mounted) setIsReady(true);
        return;
      }

      try {
        // Fetch current user; interceptor will refresh on 401 and retry
        const serv = new APIService();
        const me = await serv.getDataFromBackend("me"); // GET /api/me
        if (mounted) setUser(me?.data ?? null);
      } catch (error) {
        const issueType = error?.response?.data?.issueType;
        if (issueType === "password_expired") {
          if (mounted) setUser(null);
          redirectToExpiredPasswordReset(error?.response?.data?.email || "");
          return;
        }
        console.log("Auth bootstrap failed.");
        // swallow any bootstrap errors, user stays null
      } finally {
        if (mounted) setIsReady(true);
      }
    };

    doBootstrap();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on app load

  const loginAction = async (credentials) => {
    try {
      const serv = new APIService();
      // POST /api/signin sets HttpOnly cookies server-side
      const resp = await serv.addDataToBackend(credentials, "signin");
      const respData = resp?.data;

      // Handle legacy/incorrect 200 responses carrying error payloads
      if (respData?.status && Number(respData.status) >= 400) {
        return {
          isValid: false,
          response: {
            issueType: respData?.issueType,
            message: respData?.message || "Login failed",
          },
        };
      }

      // Immediately fetch the user profile (no localStorage)
      try {
        const me = await serv.getDataFromBackend("me");
        setUser(me?.data ?? null);
      } catch {
        // If /me fails, still consider login successful; user will refresh on next page load.
        setUser(resp?.data ?? null);
      }

      return { isValid: true };
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      if (data?.issueType && status >= 400) {
        return { isValid: false, response: { issueType: data.issueType, message: data?.message || "Login failed" } };
      }
      if (status === 401) {
        return { isValid: false, response: { issueType: "password", message: data?.message || "Invalid credentials" } };
      }
      if (status === 403) {
        return { isValid: false, response: { issueType: "email", message: data?.message || "Email not verified" } };
      }
      return { isValid: false, response: { message: data?.message || "Login failed" } };
    }
  };

  const logOut = async () => {
    try {
      const serv = new APIService();
      await serv.addDataToBackend({}, "logout"); // clears cookies server-side
    } catch {}
    setUser(null);
    // Hard redirect avoids showing protected UI after logout
    if (window.location.pathname !== "/Login") {
      window.location.replace("/Login");
    }
  };

  const value = useMemo(
    () => ({
      user,
      isReady,
      isAuthenticated: !!user,
      loginAction,
      logOut,
    }),
    [user, isReady]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
export const useAuth = () => useContext(AuthContext);
